import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import "./pagination.css";

/**
 * The one pager for every list in this app.
 *
 * Before this, 29 screens each hand-rolled the same markup under their own
 * class prefix (wx-sos-pager, wx-asg-pager, wx-wp-pager, ...), so nothing
 * kept them consistent and they had all drifted into full-size ghost
 * buttons that carried the same visual weight as a page's primary action.
 * Pagination is a secondary control; it should sit quietly under the data.
 *
 * What this adds over the copies it replaces:
 *  - compact icon buttons instead of word buttons, with the exact same
 *    accessible names ("ก่อนหน้า"/"ถัดไป") the old text carried, so screen
 *    readers and existing tests keep working
 *  - the record range ("1-20 จาก 1,300 รายการ"), which is what an operator
 *    actually wants to know; page N of M alone never answers "how far in am I"
 *  - first/last jumps once a list is long enough for stepping to be painful
 *
 * `totalCount`/`pageSize` are optional: pass them and the range line
 * appears, omit them and only the page indicator shows. Nothing is ever
 * derived when the caller cannot supply it honestly.
 */
export default function Pagination({ page, totalPages, totalCount = null, pageSize = null, onPageChange, className = "" }) {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const atFirst = page <= 1;
  const atLast = page >= safeTotalPages;
  // First/last only earn their space once stepping is genuinely tedious.
  const showJumps = safeTotalPages > 5;

  const hasRange = typeof totalCount === "number" && typeof pageSize === "number" && totalCount > 0;
  const rangeStart = hasRange ? (page - 1) * pageSize + 1 : null;
  const rangeEnd = hasRange ? Math.min(page * pageSize, totalCount) : null;

  const go = (target) => {
    const next = Math.min(safeTotalPages, Math.max(1, target));
    if (next !== page) {
      onPageChange(next);
    }
  };

  return (
    <nav className={`wx-pager ${className}`.trim()} aria-label="แบ่งหน้า">
      <p className="wx-pager-status">
        {hasRange && (
          <span className="wx-pager-range">
            {rangeStart.toLocaleString("en-US")}-{rangeEnd.toLocaleString("en-US")} จาก {totalCount.toLocaleString("en-US")} รายการ
          </span>
        )}
        <span className="wx-pager-page">
          หน้า {page} จาก {safeTotalPages}
        </span>
      </p>

      <div className="wx-pager-actions">
        {showJumps && (
          <button type="button" className="wx-pager-button" aria-label="หน้าแรก" title="หน้าแรก" disabled={atFirst} onClick={() => go(1)}>
            <ChevronsLeft size={15} aria-hidden="true" />
          </button>
        )}
        <button type="button" className="wx-pager-button" aria-label="ก่อนหน้า" title="ก่อนหน้า" disabled={atFirst} onClick={() => go(page - 1)}>
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <button type="button" className="wx-pager-button" aria-label="ถัดไป" title="ถัดไป" disabled={atLast} onClick={() => go(page + 1)}>
          <ChevronRight size={16} aria-hidden="true" />
        </button>
        {showJumps && (
          <button type="button" className="wx-pager-button" aria-label="หน้าสุดท้าย" title="หน้าสุดท้าย" disabled={atLast} onClick={() => go(safeTotalPages)}>
            <ChevronsRight size={15} aria-hidden="true" />
          </button>
        )}
      </div>
    </nav>
  );
}
